// FILE: pullRequestsRouteSearch.ts
// Purpose: The Pull Requests section's URL state — which state, project and author are being
//          shown — parsed from and written back to the route's search params.
// Layer: Route search-param contract
// Exports: PullRequestsRouteSearch, PULL_REQUEST_LIST_STATES, parsePullRequestsRouteSearch
//
// The filters live in the URL rather than in component state so a view somebody arrived at is a
// link they can send, and so a refresh lands back on the same list instead of on the default one.
// Mirrors the shape `diffRouteSearch.ts` already uses for the chat panels: one parser, called by
// `validateSearch`, that is the only place a raw search value is trusted.
//
// The page number is deliberately NOT here. It is not a filter but an accumulation — "load more"
// means the reader has three pages open, not that they are looking at page three — and a link
// carrying it would silently make the recipient's first read three times as expensive.

import type { ProjectId, PullRequestListState } from "@taskin-code/contracts";

/** Every state a listing can ask for, in the order the filter renders them. */
export const PULL_REQUEST_LIST_STATES = ["open", "merged", "closed", "all"] as const;

/** What the section shows when the URL says nothing. */
export const DEFAULT_PULL_REQUEST_LIST_STATE: PullRequestListState = "open";

/**
 * Every field optional, and absent whenever it narrows nothing — including `state` when it is the
 * default. That keeps `/pull-requests` a valid link on its own (the sidebar entry navigates to
 * exactly that) and keeps the query string down to what the reader actually chose. An unreadable
 * value is dropped rather than rejected: a URL somebody edited by hand should still show a list.
 */
export interface PullRequestsRouteSearch {
  readonly state?: PullRequestListState | undefined;
  readonly projectId?: ProjectId | undefined;
  readonly author?: string | undefined;
}

function isListState(value: unknown): value is PullRequestListState {
  return (
    typeof value === "string" && (PULL_REQUEST_LIST_STATES as ReadonlyArray<string>).includes(value)
  );
}

function normalizeSearchString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : undefined;
}

export function parsePullRequestsRouteSearch(
  search: Record<string, unknown>,
): PullRequestsRouteSearch {
  const projectId = normalizeSearchString(search.projectId) as ProjectId | undefined;
  // Bounded here rather than at the request: a login is at most 39 characters on GitHub, and an
  // unbounded value off the URL would travel to the server on every keystroke of a paste.
  const author = normalizeSearchString(search.author)?.slice(0, 39);

  const state = isListState(search.state) ? search.state : undefined;

  return {
    ...(state && state !== DEFAULT_PULL_REQUEST_LIST_STATE ? { state } : {}),
    ...(projectId ? { projectId } : {}),
    ...(author ? { author } : {}),
  };
}
